import { useState, useEffect, useRef, useCallback } from "react";
import { Sparkles, Loader2, X, Send, Check, AlertTriangle, Activity, Package, FileText, UserPlus, Wrench, Truck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/currency";
import { toast } from "sonner";

/**
 * Floating "Quick Edit" button (bottom-right by default) that opens a
 * conversational input panel. Powered by /api/horses/quick-edit (LLM-parsed).
 *
 * UX:
 *   - **Drag anywhere**: long-press the button (~250 ms) then drag to reposition;
 *     position is snapped to the nearest edge and persisted to localStorage so
 *     it survives reloads (per-device).
 *   - **Auto-collapse on scroll**: while you scroll, the button shrinks to a
 *     small icon-only puck so it never blocks page actions. It expands back
 *     ~700 ms after the last scroll event.
 *   - **Mobile-aware default**: on phones it starts above the bottom-nav
 *     drawer trigger; on desktop it starts at the classic bottom-right.
 *
 * Usage:  <QuickEditFAB onApplied={() => reloadHorses()} />
 */

// Constants ---------------------------------------------------------------

const STORAGE_KEY = "quickEditFab.pos.v1";        // {x, y, edge} in viewport %
const LONG_PRESS_MS = 250;
const COLLAPSE_AFTER_SCROLL_MS = 700;
const SAFE_PAD = 12; // px from viewport edges

// Helpers -----------------------------------------------------------------

function loadStoredPosition() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (typeof v?.xPct !== "number" || typeof v?.yPct !== "number") return null;
    return v;
  } catch {
    return null;
  }
}

function clampToViewport(x, y, w, h) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    x: Math.max(SAFE_PAD, Math.min(vw - w - SAFE_PAD, x)),
    y: Math.max(SAFE_PAD, Math.min(vh - h - SAFE_PAD, y)),
  };
}

/** Default position: bottom-right, lifted above the mobile bottom nav. */
function defaultPosition() {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Desktop reserves nothing at bottom; mobile reserves ~72px for the nav bar.
  const isMobile = vw < 1024;
  const bottomReserve = isMobile ? 88 : 24;
  const rightReserve = 24;
  // FAB collapsed size is ~56, expanded ~150. Use a safe 60 for the anchor.
  return {
    x: vw - 60 - rightReserve,
    y: vh - 60 - bottomReserve,
  };
}

// Component ----------------------------------------------------------------

export function QuickEditFAB({ onApplied }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // Drag + position state
  const [pos, setPos] = useState(() => defaultPosition());
  const [dragging, setDragging] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const fabRef = useRef(null);
  const dragState = useRef({
    longPressTimer: null,
    pointerId: null,
    startX: 0,
    startY: 0,
    originX: 0,
    originY: 0,
    moved: false,
    activated: false,
  });

  // Restore persisted position on mount; otherwise use the responsive default.
  useEffect(() => {
    const stored = loadStoredPosition();
    if (stored) {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const x = (stored.xPct * vw) / 100;
      const y = (stored.yPct * vh) / 100;
      const w = fabRef.current?.offsetWidth || 60;
      const h = fabRef.current?.offsetHeight || 60;
      setPos(clampToViewport(x, y, w, h));
    } else {
      setPos(defaultPosition());
    }
    // Re-clamp on viewport resize so the FAB never gets stranded off-screen.
    const onResize = () => {
      setPos((p) => {
        const w = fabRef.current?.offsetWidth || 60;
        const h = fabRef.current?.offsetHeight || 60;
        return clampToViewport(p.x, p.y, w, h);
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Auto-collapse on scroll. Listens on capture to catch nested scrollers.
  useEffect(() => {
    let raf = null;
    let timer = null;
    const onScroll = () => {
      if (open || dragging) return;
      if (!collapsed) {
        // Defer state update to next frame to avoid layout thrash.
        if (raf == null) {
          raf = requestAnimationFrame(() => {
            setCollapsed(true);
            raf = null;
          });
        }
      }
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setCollapsed(false), COLLAPSE_AFTER_SCROLL_MS);
    };
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      if (raf) cancelAnimationFrame(raf);
      if (timer) clearTimeout(timer);
    };
  }, [open, dragging, collapsed]);

  // Drag handlers ---------------------------------------------------------

  const beginDrag = useCallback((e) => {
    if (open) return;
    const ds = dragState.current;
    ds.pointerId = e.pointerId;
    ds.startX = e.clientX;
    ds.startY = e.clientY;
    ds.originX = pos.x;
    ds.originY = pos.y;
    ds.moved = false;
    ds.activated = false;
    // Long-press: only after ~250 ms does drag activate. A short press still
    // opens the panel via the click handler.
    if (ds.longPressTimer) clearTimeout(ds.longPressTimer);
    ds.longPressTimer = setTimeout(() => {
      ds.activated = true;
      setDragging(true);
      setCollapsed(false);
      // Haptic ping (mobile only, where supported)
      if (navigator.vibrate) {
        try { navigator.vibrate(10); } catch { /* ignore */ }
      }
    }, LONG_PRESS_MS);
    try { fabRef.current?.setPointerCapture(e.pointerId); } catch { /* ignore */ }
  }, [open, pos.x, pos.y]);

  const moveDrag = useCallback((e) => {
    const ds = dragState.current;
    if (ds.pointerId !== e.pointerId) return;
    const dx = e.clientX - ds.startX;
    const dy = e.clientY - ds.startY;
    if (!ds.moved && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
      ds.moved = true;
      // If they're dragging hard before the long-press fired, activate early.
      if (!ds.activated) {
        if (ds.longPressTimer) clearTimeout(ds.longPressTimer);
        ds.activated = true;
        setDragging(true);
        setCollapsed(false);
      }
    }
    if (!ds.activated) return;
    e.preventDefault();
    const w = fabRef.current?.offsetWidth || 60;
    const h = fabRef.current?.offsetHeight || 60;
    setPos(clampToViewport(ds.originX + dx, ds.originY + dy, w, h));
  }, []);

  const endDrag = useCallback((e) => {
    const ds = dragState.current;
    if (ds.longPressTimer) {
      clearTimeout(ds.longPressTimer);
      ds.longPressTimer = null;
    }
    try { fabRef.current?.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (ds.activated) {
      // Snap to nearest left/right edge for a clean rest position.
      const vw = window.innerWidth;
      const w = fabRef.current?.offsetWidth || 60;
      const h = fabRef.current?.offsetHeight || 60;
      setPos((p) => {
        const centerX = p.x + w / 2;
        const snapX = centerX < vw / 2 ? SAFE_PAD : vw - w - SAFE_PAD;
        const next = clampToViewport(snapX, p.y, w, h);
        // Persist as % so it survives rotation & resize.
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify({
            xPct: (next.x / window.innerWidth) * 100,
            yPct: (next.y / window.innerHeight) * 100,
          }));
        } catch { /* ignore quota errors */ }
        return next;
      });
    }
    setDragging(false);
    ds.pointerId = null;
    ds.activated = false;
  }, []);

  const onFabClick = useCallback(() => {
    // Suppress the click that follows a drag.
    if (dragState.current.moved) {
      dragState.current.moved = false;
      return;
    }
    setOpen(true);
  }, []);

  // Submit ----------------------------------------------------------------

  const submit = async () => {
    const value = text.trim();
    if (value.length < 2) {
      toast.error("Type something first");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const res = await api.post("/horses/quick-edit", { text: value });
      const r = res.data;
      setResult(r);
      const applied = r.applied || [];
      const feedLogs = r.feed_logs || [];
      const invoices = r.invoices || [];
      const staff = r.staff || [];
      const tack = r.tack || [];
      const vehicles = r.vehicles || [];
      const deleted = r.deleted || [];
      const skipped = r.skipped || [];

      const totalChanges = applied.length + feedLogs.length + invoices.length + staff.length + tack.length + vehicles.length + deleted.length;

      if (totalChanges === 0) {
        if (r.ambiguous) {
          toast.warning(r.ambiguous);
          return;
        }
        if (skipped.length) {
          const first = skipped[0];
          if (first.reason === "ambiguous match" && first.candidates?.length) {
            toast.error(`Multiple horses match "${first.input_name}": ${first.candidates.join(", ")}. Use the exact name.`);
          } else if (first.reason === "no horse match") {
            toast.error(`No horse named "${first.input_name}" found.`);
          } else if (first.reason === "feed item not specified") {
            toast.warning(`Which feed item? Choose: ${(first.candidates || ["feed", "lucerne", "teff"]).join(", ")}.`);
          } else if (first.reason === "unknown feed item") {
            toast.error(`"${first.input_name}" isn't a known feed item. Try: ${(first.candidates || []).join(", ")}.`);
          } else if (first.reason?.includes("staff")) {
            toast.error(`Staff issue: ${first.reason}${first.input_name ? ` ("${first.input_name}")` : ""}`);
          } else if (first.reason?.includes("vehicle")) {
            toast.error(`Vehicle issue: ${first.reason}${first.input_name ? ` ("${first.input_name}")` : ""}`);
          } else if (first.reason?.includes("tack")) {
            toast.error(`Tack issue: ${first.reason}${first.input_name ? ` ("${first.input_name}")` : ""}`);
          } else {
            toast.warning("No changes applied. message wasn't clear enough.");
          }
        } else {
          toast.warning("Nothing to apply. try mentioning a horse / staff / vehicle / item and what changed.");
        }
        return;
      }

      const parts = [];
      if (applied.length) parts.push(`${applied.length} horse${applied.length === 1 ? "" : "s"} updated`);
      if (feedLogs.length) parts.push(`${feedLogs.length} feed log${feedLogs.length === 1 ? "" : "s"}`);
      if (invoices.length) parts.push(`${invoices.length} invoice${invoices.length === 1 ? "" : "s"}`);
      if (staff.length) parts.push(`${staff.length} staff change${staff.length === 1 ? "" : "s"}`);
      if (tack.length) parts.push(`${tack.length} tack change${tack.length === 1 ? "" : "s"}`);
      if (vehicles.length) parts.push(`${vehicles.length} vehicle change${vehicles.length === 1 ? "" : "s"}`);
      if (deleted.length) parts.push(`${deleted.length} deleted`);
      toast.success(parts.join(" · "));

      setText("");
      onApplied?.(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Quick edit failed");
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  // While the panel is open we render the FAB at its current anchor so the
  // user knows where it lives, but visually hide it (the panel takes focus).
  const fabHidden = open;

  return (
    <>
      {/* Floating action button. absolutely positioned, draggable */}
      <button
        ref={fabRef}
        type="button"
        onClick={onFabClick}
        onPointerDown={beginDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        data-testid="quick-edit-fab"
        aria-label="Open quick edit (long-press to drag)"
        title="Tap to open. Long-press and drag to move."
        style={{
          left: `${pos.x}px`,
          top: `${pos.y}px`,
          touchAction: "none",
          opacity: fabHidden ? 0 : 1,
          pointerEvents: fabHidden ? "none" : "auto",
          transition: dragging
            ? "width 200ms ease, padding 200ms ease, border-radius 200ms ease, opacity 150ms ease"
            : "left 220ms cubic-bezier(0.2, 0.8, 0.2, 1), top 220ms cubic-bezier(0.2, 0.8, 0.2, 1), width 220ms ease, padding 220ms ease, opacity 200ms ease, transform 150ms ease",
        }}
        className={[
          "fixed z-40 flex items-center gap-2 rounded-full bg-[hsl(var(--ap-forest))] text-white shadow-xl shadow-black/20 select-none",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ap-gold))]",
          dragging ? "cursor-grabbing scale-105" : "cursor-grab active:scale-[0.97] hover:shadow-2xl",
          collapsed
            ? "h-12 w-12 px-0 justify-center"
            : "h-12 sm:h-14 px-4 sm:px-5",
        ].join(" ")}
      >
        <Sparkles className="h-5 w-5 shrink-0" />
        <span
          className={[
            "font-medium tracking-tight overflow-hidden whitespace-nowrap",
            collapsed ? "max-w-0 opacity-0" : "max-w-[160px] opacity-100",
          ].join(" ")}
          style={{ transition: "max-width 220ms ease, opacity 180ms ease" }}
        >
          Quick edit
        </span>
      </button>

      {/* Slide-up panel */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-end bg-black/40 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget && !busy) setOpen(false);
          }}
          data-testid="quick-edit-overlay"
        >
          <div className="w-full sm:w-[460px] sm:mr-6 sm:mb-0 mb-0 max-h-[92vh] flex flex-col bg-[hsl(var(--ap-paper))] border-t sm:border sm:rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-200">
            <header className="flex items-center justify-between px-5 py-3 border-b shrink-0">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[hsl(var(--ap-forest))]" />
                <h3 className="font-semibold tracking-tight">Quick edit</h3>
              </div>
              <button
                onClick={() => !busy && setOpen(false)}
                className="rounded-md p-1.5 hover:bg-black/5 transition-colors"
                data-testid="quick-edit-close"
                aria-label="Close"
                disabled={busy}
              >
                <X className="h-4 w-4" />
              </button>
            </header>

            <div className="px-5 py-4 space-y-3 shrink-0">
              <p className="text-xs text-muted-foreground leading-relaxed">
                Type changes in plain English. The AI will figure out which horses and what to update. you can stack multiple updates in one go.
              </p>
              <Textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKey}
                placeholder={
                  "e.g. Captain is injured chronic on his left foreleg.\n" +
                  "Used 4 bags of feed today, 65 bales lucerne left.\n" +
                  "Hire Sipho as a new groom, phone 0823334455.\n" +
                  "5500 rands Thabo for feed due 30th June.\n" +
                  "Delete horse Spark. sold last week.\n" +
                  "Remove staff Mosa permanently."
                }
                rows={5}
                className="text-sm leading-relaxed resize-none"
                data-testid="quick-edit-textarea"
                disabled={busy}
              />
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Press <kbd className="px-1.5 py-0.5 rounded bg-black/5 font-sans">⌘/Ctrl+↵</kbd> to apply
                </span>
                <Button
                  onClick={submit}
                  disabled={busy || text.trim().length < 2}
                  data-testid="quick-edit-submit"
                  className="bg-[hsl(var(--ap-forest))] hover:bg-[hsl(var(--ap-forest))]/90"
                >
                  {busy ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Applying…</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" /> Apply</>
                  )}
                </Button>
              </div>
            </div>

            {/* Result summary (scrollable on phones) */}
            {result && (result.applied?.length > 0 || result.feed_logs?.length > 0 || result.invoices?.length > 0 || result.staff?.length > 0 || result.tack?.length > 0 || result.vehicles?.length > 0 || result.deleted?.length > 0 || result.skipped?.length > 0) && (
              <div className="border-t bg-background/60 px-5 py-3 flex-1 overflow-y-auto space-y-2">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">What changed</div>
                {(result.applied || []).map((a, i) => (
                  <div key={`a-${i}`} className="text-xs space-y-1" data-testid={`quick-edit-applied-${i}`}>
                    <div className="flex items-center gap-2 font-medium">
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                      <span className="inline-flex items-center gap-1.5"><Activity className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />{a.horse}</span>
                    </div>
                    <ul className="pl-5 space-y-0.5 text-muted-foreground">
                      {Object.entries(a.diff || {}).map(([field, ch]) => (
                        <li key={field}>
                          <span className="capitalize">{field.replace(/_/g, " ")}</span>: {" "}
                          <span className="line-through opacity-60">{formatValue(ch.from)}</span>
                          {" → "}
                          <span className="text-foreground font-medium">{formatValue(ch.to)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {(result.feed_logs || []).map((f, i) => (
                  <div key={`f-${i}`} className="text-xs flex items-center gap-2" data-testid={`quick-edit-feed-${i}`}>
                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                    <span>
                      <span className="inline-flex items-center gap-1.5"><Package className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} /><span className="capitalize">{f.action}</span></span>{" "}
                      <span className="font-medium">{f.quantity} {f.unit}</span>{" "}
                      of <span className="capitalize">{f.item}</span>
                    </span>
                  </div>
                ))}
                {(result.invoices || []).map((inv, i) => (
                  <div key={`inv-${i}`} className="text-xs flex items-start gap-2" data-testid={`quick-edit-invoice-${i}`}>
                    <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-600" />
                    <span>
                      <span className="inline-flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />Invoice from</span> <strong>{inv.supplier}</strong>{" "}
                     . <span className="tabular-nums">{formatCurrency(inv.total, inv.currency)}</span>
                      {inv.category && <span className="ml-1 text-muted-foreground">({inv.category})</span>}
                      {inv.due_date && <span className="ml-1 text-muted-foreground">· due {inv.due_date}</span>}
                    </span>
                  </div>
                ))}
                {(result.staff || []).map((s, i) => (
                  <div key={`st-${i}`} className="text-xs space-y-1" data-testid={`quick-edit-staff-${i}`}>
                    <div className="flex items-center gap-2 font-medium">
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                      <span>
                        <span className="inline-flex items-center gap-1.5"><UserPlus className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />{s.action === "create" ? (
                          <>Hired <strong>{s.staff}</strong> as <span className="capitalize">{s.role}</span></>
                        ) : (
                          <><strong>{s.staff}</strong> updated</>
                        )}</span>
                      </span>
                    </div>
                    {s.diff && (
                      <ul className="pl-5 space-y-0.5 text-muted-foreground">
                        {Object.entries(s.diff).map(([field, ch]) => (
                          <li key={field}>
                            <span className="capitalize">{field.replace(/_/g, " ")}</span>: {" "}
                            <span className="line-through opacity-60">{formatValue(ch.from)}</span>
                            {" → "}
                            <span className="text-foreground font-medium">{formatValue(ch.to)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                {(result.tack || []).map((t, i) => (
                  <div key={`tk-${i}`} className="text-xs space-y-1" data-testid={`quick-edit-tack-${i}`}>
                    <div className="flex items-center gap-2 font-medium">
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                      <span>
                        <span className="inline-flex items-center gap-1.5"><Wrench className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />{t.action === "create" ? (
                          <>Added <strong>{t.tack}</strong> ({t.type}, qty {t.qty})</>
                        ) : (
                          <><strong>{t.tack}</strong> updated</>
                        )}</span>
                      </span>
                    </div>
                    {t.diff && (
                      <ul className="pl-5 space-y-0.5 text-muted-foreground">
                        {Object.entries(t.diff).map(([field, ch]) => (
                          <li key={field}>
                            <span className="capitalize">{field.replace(/_/g, " ")}</span>: {" "}
                            <span className="line-through opacity-60">{formatValue(ch.from)}</span>
                            {" → "}
                            <span className="text-foreground font-medium">{formatValue(ch.to)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                {(result.vehicles || []).map((v, i) => (
                  <div key={`vh-${i}`} className="text-xs space-y-1" data-testid={`quick-edit-vehicle-${i}`}>
                    <div className="flex items-center gap-2 font-medium">
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                      <span>
                        <span className="inline-flex items-center gap-1.5"><Truck className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />{v.action === "maintenance" ? (
                          <>Maintenance on <strong>{v.vehicle}</strong>: {v.description}{v.cost != null ? ` · R${Number(v.cost).toLocaleString()}` : ""}</>
                        ) : (
                          <><strong>{v.vehicle}</strong> updated</>
                        )}</span>
                      </span>
                    </div>
                    {v.diff && (
                      <ul className="pl-5 space-y-0.5 text-muted-foreground">
                        {Object.entries(v.diff).map(([field, ch]) => (
                          <li key={field}>
                            <span className="capitalize">{field.replace(/_/g, " ")}</span>: {" "}
                            <span className="line-through opacity-60">{formatValue(ch.from)}</span>
                            {" → "}
                            <span className="text-foreground font-medium">{formatValue(ch.to)}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                {(result.deleted || []).map((d, i) => (
                  <div key={`del-${i}`} className="text-xs flex items-center gap-2 text-red-700" data-testid={`quick-edit-deleted-${i}`}>
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    <span className="inline-flex items-center gap-1.5">
                      <Trash2 className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                      Deleted {d.entity}: <strong>{d.name}</strong>
                      {d.reason && <span className="text-muted-foreground italic ml-1">. {d.reason}</span>}
                    </span>
                  </div>
                ))}
                {result.ambiguous && (result.applied?.length || result.feed_logs?.length || result.invoices?.length || result.staff?.length || result.tack?.length || result.vehicles?.length || result.deleted?.length) > 0 && (
                  <div className="text-xs flex items-start gap-2 text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span><strong>Note:</strong> {result.ambiguous}</span>
                  </div>
                )}
                {(result.skipped || []).map((s, i) => (
                  <div key={`s-${i}`} className="text-xs flex items-start gap-2 text-amber-700">
                    <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>
                      Skipped <strong>{s.input_name || s.horse || "?"}</strong>. {s.reason}
                      {s.candidates?.length ? ` (matches: ${s.candidates.join(", ")})` : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function formatValue(v) {
  if (v === null || v === undefined || v === "") return "";
  if (v === true) return "yes";
  if (v === false) return "no";
  return String(v);
}
